import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    VStack,
    HStack,
    Text,
    Badge,
    IconButton,
    Spinner,
    Table,
    Flex,
    Button,
} from "@chakra-ui/react";
import { FiArrowLeft } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { CustomPagination } from "../Components/custom/CustomPagination";
import { getPartnerAccountData } from "../Redux/Action";
import { IoChevronBackOutline } from "react-icons/io5";
import { FaRegCopy } from "react-icons/fa6";
import { IoCheckmarkDoneOutline } from "react-icons/io5";
import { setDynamicTitle } from "../Utils/dynamicTitleManager";
import { AddUserUnderIBModal } from "../Components/custom/AddUserUnderIBModal";

export const IBDetailPage = ({ ibData, onBack }) => {
    // Use dynamic title management
    useEffect(() => {
        setDynamicTitle("IB Details");
    }, []);

    const dispatch = useDispatch();
    const [partnerAccounts, setPartnerAccounts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [copiedField, setCopiedField] = useState("");
   
    // Modal state for adding user under IB
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // pagination state (partner accounts only)
    const [currentPage, setCurrentPage] = useState(1);
    const [rowCount] = useState(10);
    const [totalPagesCount, setTotalPagesCount] = useState(0);
    function formatDateDDMMYY_AMPM(isoDate) {
        if (!isoDate) return "--";
        const d = new Date(isoDate);
        if (Number.isNaN(d.getTime())) return "--";

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = String(d.getFullYear()).slice(-2);

        let hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        if (hours === 0) hours = 12;
        const hoursStr = String(hours).padStart(2, "0");

        return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
    }
    // reset to page 1 if IB changes
    useEffect(() => {
        setCurrentPage(1);
    }, [ibData?.referralCode]);

    useEffect(() => {
        if (!ibData?.referralCode) return;

        setIsLoading(true);

        const payload = {
            currentPage,
            rowCount,
            referralCode: ibData.referralCode,
        };

        dispatch(
            getPartnerAccountData({
                payload,
                callback: (res) => {
                    console.log("res", res);
                    const code = res?.code;
                    const accounts = res?.data?.data ?? [];
                    console.log("accounts", accounts);
                    const pagination = res?.data?.pagination ?? {};
                    console.log("accounts",accounts)
                    if (code === 200) {
                        setPartnerAccounts(accounts);
                        setTotalPagesCount(Number(pagination.totalPages) || 0);
                    } else {
                        setPartnerAccounts([]);
                        setTotalPagesCount(0);
                    }

                    setIsLoading(false);
                },
            })
        );
    }, [dispatch, ibData?.referralCode, currentPage, rowCount, refreshTrigger]);


    const startIndex = useMemo(
        () => (currentPage - 1) * rowCount,
        [currentPage, rowCount]
    );

    if (!ibData) return null;

    return (
        <>
        <VStack spacing={8} align="stretch" width="100%" px={{ base: 4, md: 8 }} py={6}>
            {/* Header */}
            <HStack justifyContent="space-between" alignItems="center">
                <IconButton
                    aria-label="Back"
                    icon={<FiArrowLeft />}
                    onClick={onBack}
                    size="md"
                    borderRadius="lg"
                    colorScheme="blue"
                    variant="solid"
                    boxShadow="sm"
                >

                    <IoChevronBackOutline />
                </IconButton>
                <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color="blue.600">
                    IB Details
                </Text>
            </HStack>

            {/* IB Info Card (with visible gradient glow) */}
            <Box
                position="relative"
                borderRadius="2xl"
                p={{ base: 6, md: 8 }}
                overflow="hidden"
                boxShadow="2xl"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
            >
                {/* --- Decorative Layer: Top-Left Bubble Glow --- */}
                <Box
                    position="absolute"
                    top="-30%"
                    left="-30%"
                    w="70%"
                    h="70%"
                    pointerEvents="none"
                    zIndex={0}
                    filter="blur(80px)"
                    opacity={0.9}
                    backgroundImage="radial-gradient(circle at 30% 30%, rgba(59,130,246,0.35), rgba(59,130,246,0) 60%)"
                />

                {/* --- Decorative Layer: Bottom-Right Bubble Glow --- */}
                <Box
                    position="absolute"
                    bottom="-30%"
                    right="-30%"
                    w="70%"
                    h="70%"
                    pointerEvents="none"
                    zIndex={0}
                    filter="blur(80px)"
                    opacity={0.8}
                    backgroundImage="radial-gradient(circle at 70% 70%, rgba(147,197,253,0.30), rgba(147,197,253,0) 60%)"
                />

                {/* --- Decorative Layer: Diagonal soft tint so glows stay visible on white --- */}
                <Box
                    position="absolute"
                    inset={0}
                    pointerEvents="none"
                    zIndex={0}
                    opacity={0.35}
                    backgroundImage="linear-gradient(to bottom right, rgba(59,130,246,0.06), rgba(255,255,255,0.06))"
                />

                {/* --- Decorative Layer: Center “square shades” (rotated cards) --- */}
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%) rotate(12deg)"
                    w={{ base: "260px", md: "340px" }}
                    h={{ base: "260px", md: "340px" }}
                    pointerEvents="none"
                    zIndex={0}
                    borderRadius="2xl"
                    opacity={0.25}
                    border="1px solid"
                    borderColor="blue.100"
                    backgroundImage="linear-gradient(to bottom right, rgba(219,234,254,0.7), rgba(255,255,255,0.9))" /* blue.100 -> white */
                    boxShadow="lg"
                />
                <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%) rotate(-8deg)"
                    w={{ base: "180px", md: "240px" }}
                    h={{ base: "180px", md: "240px" }}
                    pointerEvents="none"
                    zIndex={0}
                    borderRadius="2xl"
                    opacity={0.25}
                    border="1px solid"
                    borderColor="purple.100"
                    backgroundImage="linear-gradient(to bottom right, rgba(233,213,255,0.55), rgba(255,255,255,0.9))" /* purple.100 -> white */
                    boxShadow="md"
                />

                {/* --- Content --- */}
                <Box position="relative" zIndex={1}>


                    <Flex
                        flexWrap="wrap"
                        gap={{ base: 6, md: 8 }}
                        justify="space-between"
                    >
                        {/* Full Name */}
                        <Box
                            flex={{ base: "1 1 45%", md: "1 1 30%" }}
                            bg="white"
                            p={4}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            <Text fontSize="sm" color="gray.500" mb={1}>
                                Full Name
                            </Text>
                            <HStack>
                                <Text fontWeight="semibold" fontSize="md" color="gray.700">
                                    {ibData.fname} {ibData.lname}
                                </Text>
                                <IconButton
                                    aria-label="Copy Full Name"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                        navigator.clipboard.writeText(`${ibData.fname} ${ibData.lname}`);
                                        setCopiedField("fullName");
                                        setTimeout(() => setCopiedField(""), 1000);
                                    }}
                                >
                                    {copiedField === "fullName" ? <IoCheckmarkDoneOutline color="#000" /> : <FaRegCopy color="#000" />}
                                </IconButton>
                            </HStack>
                        </Box>

                        {/* Email */}
                        <Box
                            flex={{ base: "1 1 45%", md: "1 1 30%" }}
                            bg="white"
                            p={4}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            <Text fontSize="sm" color="gray.500" mb={1}>
                                Email
                            </Text>
                            <HStack>
                                <Text fontWeight="semibold" fontSize="md" color="gray.700">
                                    {ibData.email}
                                </Text>
                                <IconButton
                                    aria-label="Copy Email"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                        navigator.clipboard.writeText(ibData.email);
                                        setCopiedField("email");
                                        setTimeout(() => setCopiedField(""), 1000);
                                    }}
                                >
                                    {copiedField === "email" ? <IoCheckmarkDoneOutline color="#000" /> : <FaRegCopy color="#000" />}
                                </IconButton>
                            </HStack>
                        </Box>

                        {/* Mobile */}
                        <Box
                            flex={{ base: "1 1 45%", md: "1 1 30%" }}
                            bg="white"
                            p={4}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            <Text fontSize="sm" color="gray.500" mb={1}>
                                Mobile
                            </Text>
                            <HStack>
                                <Text fontWeight="semibold" fontSize="md" color="gray.700">
                                    {ibData.mobile}
                                </Text>
                                <IconButton
                                    aria-label="Copy Mobile"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                        navigator.clipboard.writeText(ibData.mobile);
                                        setCopiedField("mobile");
                                        setTimeout(() => setCopiedField(""), 1000);
                                    }}
                                >
                                    {copiedField === "mobile" ? <IoCheckmarkDoneOutline color="#000" /> : <FaRegCopy color="#000" />}
                                </IconButton>
                            </HStack>
                        </Box>

                        {/* Balance */}
                        <Box
                            flex={{ base: "1 1 45%", md: "1 1 30%" }}
                            bg="white"
                            p={4}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            <Text fontSize="sm" color="gray.500" mb={1}>
                                Balance
                            </Text>
                            <Text fontWeight="semibold" fontSize="md" color="gray.700">
                                ${ibData.balance}
                            </Text>
                        </Box>
                          <Box
                            flex={{ base: "1 1 45%", md: "1 1 30%" }}
                            bg="white"
                            p={4}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            <Text fontSize="sm" color="gray.500" mb={1}>
                                Currency
                            </Text>
                            <Text fontWeight="semibold" fontSize="md" color="gray.700">
                            {ibData.currency}
                            </Text>
                        </Box>
                          <Box
                            flex={{ base: "1 1 45%", md: "1 1 30%" }}
                            bg="white"
                            p={4}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            <Text fontSize="sm" color="gray.500" mb={1}>
                                MT5 Balance
                            </Text>
                            <Text fontWeight="semibold" fontSize="md" color="gray.700">
                            ${ibData?.mt5balance}
                            </Text>
                        </Box>

                        {/* Referral Code */}
                        <Box
                            flex={{ base: "1 1 45%", md: "1 1 30%" }}
                            bg="white"
                            p={4}
                            borderRadius="xl"
                            boxShadow="md"
                            border="1px solid"
                            borderColor="gray.200"
                        >
                            <Text fontSize="sm" color="gray.500" mb={1}>
                                Referral Code
                            </Text>
                            <Badge colorScheme="purple" fontSize="sm">
                                {ibData.referralCode}
                            </Badge>
                        </Box>
                    </Flex>
                </Box>
            </Box>


            {/* IB Accounts Table */}
            <Box>
                <Text fontSize="xl" fontWeight="bold" mb={2} color="blue.600">
                    LIVE ACCOUNTS
                </Text>
                <Box overflowX="auto" borderRadius="lg" boxShadow="md">
                    <Table.Root size="sm" variant="unstyled" width="100%">
                        <Table.Header>
                            <Table.Row bg="blue.50">
                                <Table.ColumnHeader textAlign="center">S/N</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Balance</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Account No.</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Mobile</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {ibData.ibAccounts?.length > 0 ? (
                                ibData.ibAccounts.map((account, index) => (
                                    <Table.Row key={account._id} _hover={{ bg: "blue.50" }} cursor="default">
                                        <Table.Cell textAlign="center">{index + 1}</Table.Cell>
                                        <Table.Cell textAlign="center">{account.balance}</Table.Cell>
                                        <Table.Cell textAlign="center">{account.accountNo}</Table.Cell>
                                        <Table.Cell textAlign="center">{ibData.mobile}</Table.Cell>
                                    </Table.Row>
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={4} textAlign="center" py={4}>
                                        No IB accounts available
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table.Root>
                </Box>
            </Box>

            {/* Partner Accounts Table + Pagination */}
            <Box>
                <HStack justifyContent="space-between" mb={2}>
                    <Text fontSize="xl" fontWeight="bold" color="gray.700">
                        PARTNER ACCOUNTS
                    </Text>
                    <HStack spacing={3}>
                        {/* top-right loader for extra feedback */}
                        {isLoading && <Spinner size="sm" />}
                        <Button
                            size="sm"
                            colorScheme="green"
                            onClick={() => setIsAddUserModalOpen(true)}
                            leftIcon={<Text fontSize="lg" fontWeight="bold">+</Text>}
                        >
                            Add User+
                        </Button>
                    </HStack>
                </HStack>

                <Box overflowX="auto" borderRadius="lg" boxShadow="md">
                    <Table.Root size="sm" variant="unstyled" width="100%">
                        <Table.Header>
                            <Table.Row bg="gray.100">
                                <Table.ColumnHeader textAlign="center">S/N</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Full Name</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Balance</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Account No.</Table.ColumnHeader>
                                <Table.ColumnHeader textAlign="center">Mobile</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {/* Inline table loader row */}
                            {isLoading ? (
                                <Table.Row>
                                    <Table.Cell colSpan={5} textAlign="center" py={6}>
                                        <HStack spacing={3} justify="center">
                                            <Spinner />
                                            <Text color="gray.600" fontSize="sm">
                                                Loading partner accounts…
                                            </Text>
                                        </HStack>
                                    </Table.Cell>
                                </Table.Row>
                            ) : partnerAccounts?.length > 0 ? (
                                partnerAccounts.map((item, idx) => (
                                    
                                    <Table.Row
                                        key={item._id}
                                        bg={idx % 2 === 0 ? "gray.50" : "white"}
                                        _hover={{ bg: "gray.100" }}
                                        cursor="default"
                                    >
                                        <Table.Cell textAlign="center">{startIndex + idx + 1}</Table.Cell>
                                        <Table.Cell textAlign="center">
                                            {item.fname} {item.lname}
                                        </Table.Cell>
                                        <Table.Cell textAlign="center">{Number(item.balance || 0)}</Table.Cell>
                                        <Table.Cell textAlign="center">{item.accountno}</Table.Cell>
                                        <Table.Cell textAlign="center">{item.mobile}</Table.Cell>
                                    </Table.Row>
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={5} textAlign="center" py={6}>
                                        No partner account available
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table.Root>
                </Box>

                {/* Pagination (only for partner accounts) */}
                <Flex pt={4} justifyContent="center">
                    <CustomPagination
                        totalCount={totalPagesCount}
                        current={currentPage}
                        setCurrent={setCurrentPage}
                        isLoading={isLoading}
                    />
                </Flex>
            </Box>
        </VStack>

        {/* Add User Under IB Modal */}
        {isAddUserModalOpen && (
          <AddUserUnderIBModal
            isOpen={isAddUserModalOpen}
            onClose={() => setIsAddUserModalOpen(false)}
            ibData={ibData}
            refetchData={() => {
              setRefreshTrigger(prev => prev + 1);
              setCurrentPage(1);
            }}
          />
        )}
        </>
    );
};